/**
 * PL-Line filter rail.
 *
 * The listing's filter controls have exactly one presentation: the rail. A fixed
 * vertical capsule in the page's left margin on desktop, laid down as a bottom
 * bar below 1181px. There is no second, horizontal toolbar at the top of the
 * page — `.pl-railed` goes on at first paint and stays on, so
 * `snippets/pl-facet-rail-styles` lays the controls out as the rail from the
 * start and the page never shows two filter UIs.
 *
 * One set of controls, one form, one presentation. A second copy of the inputs
 * would be serialized by the same `facets-form-component` and desync the applied
 * filters, so nothing here clones or moves DOM — it only sets a class.
 *
 * Two things CSS cannot express, and both are why this file exists:
 *
 * 1. `<overflow-list>` collapses filters behind a "More" button based on measured
 *    width, which in a 56px rail would swallow all of them. Its `disabled`
 *    attribute returns every item to the default slot and hides "More"
 *    (`assets/overflow-list.js`, `assets/overflow-list.css`), so the rail turns
 *    it off and keeps it off.
 *
 * 2. Applying a filter re-renders the section and morphs it in. `class` is not on
 *    morph's preserved list (`assets/morph.js:16`), so the re-render wipes both
 *    `.pl-railed` and `disabled` off the live DOM — which, now that the rail is
 *    the only filter control, would take filtering off the page entirely until
 *    the next event. Morph updates the nodes in place rather than replacing them,
 *    so observing them is enough to put both back.
 */

/** Set on the wrapper to lay the controls out as the rail. Never removed. */
const RAILED_CLASS = 'pl-railed';

/** The listing toolbar. Excludes the mobile filter drawer, which is left alone. */
const WRAPPER_SELECTOR = '.facets-block-wrapper:not(#filters-drawer)';

/** @type {HTMLElement | null} */
let wrapper = null;

/** @type {Element | null} */
let overflowList = null;

/** Watches for a re-render wiping `.pl-railed` off the wrapper. */
/** @type {MutationObserver | null} */
let wrapperObserver = null;

/** Watches for the overflow list collapsing filters behind "More". */
/** @type {MutationObserver | null} */
let collapseObserver = null;

/**
 * Puts every filter back on the default slot.
 *
 * `disabled` only resets the list when the attribute *changes*, so re-asserting
 * it means removing it first. Both halves run synchronously out of
 * `attributeChangedCallback`, so the reflow the removal provokes is undone in
 * the same task and never paints.
 *
 * @param {Element} list
 */
const resetOverflowList = (list) => {
  list.removeAttribute('disabled');
  list.setAttribute('disabled', 'true');
};

/**
 * Resolves once `<overflow-list>` has wired the internals `disabled` acts on.
 *
 * Its reset reads refs that `connectedCallback` only assigns after awaiting the
 * shadow stylesheet, and it holds no default — so setting `disabled` before then
 * throws inside the element's own reaction. Deferred module scripts do not wait
 * on a stylesheet inside a declarative shadow root, so on a cold cache this
 * script really can win that race.
 *
 * Waiting on the same two things the element waits on closes it. The order is
 * deterministic rather than lucky: the element registered its `load` listener
 * during upgrade, before this file ran, and listeners fire in registration
 * order, so its own continuation is always ahead of ours.
 *
 * @param {Element} list
 */
const whenListReady = async (list) => {
  await customElements.whenDefined('overflow-list');

  const styles = list.shadowRoot?.querySelector('link[rel="stylesheet"]');
  if (!styles || styles.sheet) return;

  await new Promise((resolve) => {
    styles.addEventListener('load', resolve, { once: true });
    styles.addEventListener('error', resolve, { once: true });
  });
};

/**
 * Holds the list open for as long as the rail is out — which is always.
 *
 * The rail is applied before the list has measured itself, so the list sees no
 * room for its filters and collapses every one of them behind "More", and
 * `:host([disabled])` then hides "More" too, leaving the rail with nothing but
 * Sort. Setting `disabled` undoes that, but a reflow queued before the reset can
 * still land after it and re-collapse the row. Rather than guess how long that
 * takes, watch the slot assignments and undo any collapse that appears.
 * Resetting only when an item has actually moved is what stops this chasing its
 * own tail.
 *
 * @param {Element} list
 */
const observeCollapse = (list) => {
  collapseObserver?.disconnect();

  collapseObserver = new MutationObserver(() => {
    const collapsed = Array.from(list.children).some((item) => item.slot === 'overflow');
    if (collapsed) resetOverflowList(list);
  });

  collapseObserver.observe(list, { attributeFilter: ['slot'], subtree: true });
};

/**
 * Re-asserts the rail on the current DOM.
 *
 * Every write here is guarded by a read, because this runs as its own observer's
 * callback and an unconditional write would feed itself.
 *
 * `classList.add` is the trap: adding a token that is ALREADY present still
 * writes the attribute. `DOMTokenList.add()` always runs the update steps, which
 * "set an attribute value", which queues a mutation record even when the value
 * does not change — so the naive form re-triggers this callback, which writes
 * again, forever. Microtasks drain before the task ends, so the page never gets
 * to paint: it hangs and the tab dies. Verified in jsdom, not assumed.
 *
 * The `contains` check makes the no-op case a true no-op. Horizon's own
 * `overflow-list` guards its slot writes the same way (`assets/overflow-list.js`,
 * `#reflowItems`), which is why its observers do not chase themselves either.
 */
const enforce = () => {
  if (!wrapper) return;

  if (!wrapper.classList.contains(RAILED_CLASS)) wrapper.classList.add(RAILED_CLASS);

  const list = wrapper.querySelector('overflow-list');
  if (!list) return;

  if (list !== overflowList) {
    overflowList = list;
    observeCollapse(list);
  }

  // Gated even after a re-render, where the element is long since ready and this
  // resolves on the spot. Re-entry is harmless: the reset is idempotent.
  if (list.getAttribute('disabled') !== 'true') {
    whenListReady(list).then(() => resetOverflowList(list));
  }
};

/**
 * Binds to the toolbar currently in the document.
 *
 * Re-run when the theme editor swaps the section out, which replaces the nodes
 * outright rather than morphing them.
 */
const setup = () => {
  const next = /** @type {HTMLElement | null} */ (document.querySelector(WRAPPER_SELECTOR));

  if (next !== wrapper) {
    wrapperObserver?.disconnect();
    wrapperObserver = null;
    collapseObserver?.disconnect();
    collapseObserver = null;
    overflowList = null;
    wrapper = next;
  }

  if (!wrapper) return;

  if (!wrapperObserver) {
    wrapperObserver = new MutationObserver(enforce);
    wrapperObserver.observe(wrapper, { attributeFilter: ['class'] });
  }

  enforce();
};

setup();

document.addEventListener('shopify:section:load', setup);
document.addEventListener('shopify:section:unload', setup);
